import React, {Fragment, useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {connect} from "react-redux";
import DatasetFilterAttributeIcon from "../dataset-filter-attribute-icon";
import DatasetFilters from "./DatasetFilters";
import {
  getDimensionAttributeMap,
  getDimensionFilterValues,
  getDimensionLabel as getDimensionLabelFromJsonStat,
  getFormattedDimensionLabel,
  getFormattedDimensionValueLabel,
  getMarginalDimensions,
  MARGINAL_DIMENSION_KEY
} from "../../utils/dataset";
import {localizeI18nObj} from "../../utils/i18n";
import {getFilterTreeNode, isMultiSelectDim} from "./utils";

const mapStateToProps = ({app, appConfig}) => ({
  appLanguage: app.language,
  languages: app.languages,
  hiddenDimensionValueLabels: appConfig.hiddenDimensionValueLabels
});

const DatasetFiltersJsonStatWrapper = ({
  appLanguage,
  languages,
  hiddenDimensionValueLabels,
  jsonStat,
  timeDim,
  layout,
  filterTree,
  labelFormat,
  onSelect,
  onCriteriaShow,
  hideMultiSelectDims = false,
  enclosingContainerId,
  localizedTimePeriodFormatMapExternal = undefined
}) => {
  const {t} = useTranslation();

  const dimAttributeMap = useMemo(
    () => (jsonStat && (jsonStat?.id || []).length > 0 ? getDimensionAttributeMap(jsonStat, t) : null),
    [jsonStat, t]
  );

  const getIsDimensionAllowed = useCallback(
    dim =>
      (isMultiSelectDim(dim, layout) && !hideMultiSelectDims) ||
      (!isMultiSelectDim(dim, layout) && jsonStat.size[jsonStat.id.indexOf(dim)] !== 1),
    [jsonStat, layout, hideMultiSelectDims]
  );

  const getDimensionLabel = useCallback(
    dimension => getDimensionLabelFromJsonStat(jsonStat, null, dimension, t),
    [jsonStat, t]
  );

  const getFilterValues = useCallback(
    dim => getDimensionFilterValues(dim, jsonStat, layout, filterTree),
    [jsonStat, layout, filterTree]
  );

  const getFormattedName = useCallback(
    (dimension, value, labelFormat, withAttribute) => {
      let valueLabel;

      if (dimension !== MARGINAL_DIMENSION_KEY) {
        valueLabel = getFormattedDimensionValueLabel(
          jsonStat,
          null,
          dimension,
          value,
          labelFormat,
          t,
          localizedTimePeriodFormatMapExternal
        );
      } else {
        const localizedHiddenDimensionValueLabels =
          localizeI18nObj(hiddenDimensionValueLabels, appLanguage, languages) || [];
        const hiddenLabels = localizedHiddenDimensionValueLabels.map(label => label.toLowerCase());

        const marginal = jsonStat.extension.marginalvalues[value];
        valueLabel = marginal.label
          ? marginal.label
          : getMarginalDimensions(jsonStat, marginal.datasetid)
              .map(dim =>
                getFormattedDimensionValueLabel(
                  jsonStat,
                  marginal.datasetid,
                  dim,
                  marginal.dimensionvalues[dim],
                  labelFormat,
                  t,
                  localizedTimePeriodFormatMapExternal
                )
              )
              .filter(label => !hiddenLabels.includes(label.toLowerCase()))
              .join(", ");
      }

      const datasetId = jsonStat.extension.datasets[0];

      return !withAttribute ? (
        valueLabel
      ) : (
        <Fragment>
          {valueLabel}
          {withAttribute && (
            <DatasetFilterAttributeIcon
              dimension={dimension}
              dimensionValues={[value]}
              datasetDimAttributeMap={dimAttributeMap?.[datasetId]}
              labelFormat={labelFormat}
              disabled
              getDimValueLabel={dimVal =>
                getFormattedDimensionValueLabel(
                  jsonStat,
                  datasetId,
                  dimension,
                  dimVal,
                  labelFormat,
                  undefined,
                  localizedTimePeriodFormatMapExternal
                )
              }
            />
          )}
        </Fragment>
      );
    },
    [
      appLanguage,
      languages,
      hiddenDimensionValueLabels,
      jsonStat,
      dimAttributeMap,
      t,
      localizedTimePeriodFormatMapExternal
    ]
  );

  const getFormattedTooltip = useCallback(
    (dimension, value, labelFormat) => (
      <div style={{fontSize: 13}}>
        {(() => {
          if (dimension !== MARGINAL_DIMENSION_KEY) {
            return getFormattedDimensionValueLabel(
              jsonStat,
              null,
              dimension,
              value,
              labelFormat,
              t,
              localizedTimePeriodFormatMapExternal
            );
          } else {
            const marginal = jsonStat.extension.marginalvalues[value];
            if (marginal.label) {
              return marginal.label;
            } else {
              return getMarginalDimensions(jsonStat, marginal.datasetid).map((dim, idx) => {
                const dimLabel = getFormattedDimensionLabel(jsonStat, marginal.datasetid, dim, labelFormat, t);
                const valueLabel = getFormattedDimensionValueLabel(
                  jsonStat,
                  marginal.datasetid,
                  dim,
                  marginal.dimensionvalues[dim],
                  labelFormat,
                  t,
                  localizedTimePeriodFormatMapExternal
                );
                return `${dimLabel}: ${valueLabel}`;
              });
            }
          }
        })()}
      </div>
    ),
    [jsonStat, t, localizedTimePeriodFormatMapExternal]
  );

  const handleFilterSelect = useCallback(
    (dimension, value) => {
      const orderedValue = Array.isArray(value)
        ? jsonStat.dimension[dimension].category.index.filter(dimVal => value.includes(dimVal))
        : value;

      onSelect(dimension, orderedValue);
    },
    [jsonStat, onSelect]
  );

  const getDimensionIsHierarchical = useCallback(
    dim => {
      const childMap = jsonStat?.dimension[dim]?.category?.child || {};
      return Object.keys(childMap).length > 0;
    },
    [jsonStat]
  );

  const getTreeFilterValues = useCallback(
    dimension => {
      const tree = [];
      const dimensionCategory = jsonStat.dimension[dimension].category;
      const childMap = dimensionCategory?.child || {};
      const additionalitemslabels = jsonStat?.extension?.additionalitemslabels?.[dimension] || {};

      const orderedRootItems = [...(jsonStat?.extension?.orderedrootitems?.[dimension] || [])];
      const expandedChildMap = Object.entries(childMap).reduce((acc, [key, values]) => {
        acc[key] = true;
        values.forEach(value => {
          acc[value] = true;
        });
        return acc;
      }, {});
      (dimensionCategory?.index || []).forEach(id => {
        if (expandedChildMap[id] !== true) {
          orderedRootItems.push(id);
        }
      });

      orderedRootItems.forEach(itemId => {
        const node = getFilterTreeNode(itemId, undefined, childMap, dimensionCategory, additionalitemslabels);
        tree.push(node);
      });

      return tree;
    },
    [jsonStat]
  );

  return (
    jsonStat && (
      <DatasetFilters
        jsonStat={jsonStat}
        timeDim={timeDim}
        layout={layout}
        filterTree={filterTree}
        labelFormat={labelFormat}
        getIsDimensionAllowed={getIsDimensionAllowed}
        getDimensionLabel={getDimensionLabel}
        getFilterValues={getFilterValues}
        getTreeFilterValues={getTreeFilterValues}
        getDimensionIsHierarchical={getDimensionIsHierarchical}
        getFormattedName={getFormattedName}
        getFormattedTooltip={getFormattedTooltip}
        dimAttributeMap={dimAttributeMap}
        onSelect={handleFilterSelect}
        onCriteriaShow={onCriteriaShow}
        enclosingContainerId={enclosingContainerId}
        localizedTimePeriodFormatMapExternal={localizedTimePeriodFormatMapExternal}
      />
    )
  );
};

export default connect(mapStateToProps)(DatasetFiltersJsonStatWrapper);
